import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { pages } from '../../components/utils/pages-links';
import BackButton from '../../components/ui/BackNavigationButton';
import NavigationButton from '../../components/ui/NavigationButton';
import TutorialOverlay from '../../components/ui/TutorialOverlay';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../components/ui/ToolTip';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { usePageTooltips } from '../../hooks/usePageTooltips';
import Select from '../../components/forms/scheduleForm/Select';
import SelectItem from '../../components/forms/scheduleForm/SelectItem';
import SelectTrigger from '../../components/forms/scheduleForm/SelectTrigger';
import SelectContent from '../../components/forms/scheduleForm/SelectContent';
import SelectValue from '../../components/forms/scheduleForm/SelectValue';
import ScenarioIndicator from '../../components/forms/scenario/ScenarioIndicator';

const TransitionsPage = () => {
  const [schedules, setSchedules] = useState([]);
  const [sequenceLength, setSequenceLength] = useState('2');
  const [transitions, setTransitions] = useState([]);
  const [transitions3, setTransitions3] = useState([]);
  const [transitions4, setTransitions4] = useState([]);
  const [transitions5, setTransitions5] = useState([]);
  const [penalizedTransitions, setPenalizedTransitions] = useState([]);
  const [currentTransition, setCurrentTransition] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  
  const formRef = useRef(null);
  const transitionsTableRef = useRef(null);
  const nextButtonRef = useRef(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const tooltips = usePageTooltips();
  const isExampleMode = location.state?.isExample;

  const tutorialSteps = [
    {
      title: t("tutorial.transitions.title") || "Shift Transitions",
      description: t("tutorial.transitions.description") || "Here you can define which sequences of shifts should not occur together. For example, you may want to prevent a night shift followed immediately by a morning shift.",
      position: "center"
    },
    {
      title: t("tutorial.transitions.form.title") || "Define Transitions",
      description: t("tutorial.transitions.form.description") || "Select the sequence length and then choose the shifts that should not follow each other.",
      position: "top-right",
      elementRef: formRef
    },
    {
      title: t("tutorial.transitions.table.title") || "Transitions Table",
      description: t("tutorial.transitions.table.description") || "View and manage all the transition rules you've created.",
      position: "top-right",
      elementRef: transitionsTableRef
    },
    {
      title: t("tutorial.transitions.next.title") || "Ready to Continue",
      description: t("tutorial.transitions.next.description") || "When you're done defining transitions, click Next to continue to the next step.",
      position: "next-button",
      elementRef: nextButtonRef
    }
  ];

  useEffect(() => {
    if (isExampleMode) {
      setShowTutorial(true);
    }
    
    loadData();
  }, [isExampleMode]);

  const loadData = () => {
    try {
      const savedSchedules = JSON.parse(sessionStorage.getItem('schedules') || '[]');
      setSchedules(savedSchedules);
      
      setCurrentTransition(Array(parseInt(sequenceLength)).fill(0));
      
      const savedTransitions = JSON.parse(sessionStorage.getItem('penalizedTransitions') || '[]');
      const savedTransitions3 = JSON.parse(sessionStorage.getItem('penalizedTransitions_3') || '[]');
      const savedTransitions4 = JSON.parse(sessionStorage.getItem('penalizedTransitions_4') || '[]');
      const savedTransitions5 = JSON.parse(sessionStorage.getItem('penalizedTransitions_5') || '[]');
      const savedPenalizedTransitions = JSON.parse(sessionStorage.getItem('penalizedTransitions_shifts') || '[]');
      
      setTransitions(savedTransitions);
      setTransitions3(savedTransitions3);
      setTransitions4(savedTransitions4);
      setTransitions5(savedTransitions5);
      setPenalizedTransitions(savedPenalizedTransitions);
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.loadDataError'),
        variant: 'destructive'
      });
      setIsLoading(false);
    }
  };

  const saveTransitions = async () => {
    try {
      if (!isExampleMode) {
        sessionStorage.setItem('penalizedTransitions', JSON.stringify(transitions));
        sessionStorage.setItem('penalizedTransitions_3', JSON.stringify(transitions3));
        sessionStorage.setItem('penalizedTransitions_4', JSON.stringify(transitions4));
        sessionStorage.setItem('penalizedTransitions_5', JSON.stringify(transitions5));
        sessionStorage.setItem('penalizedTransitions_shifts', JSON.stringify(penalizedTransitions));

        const savedTransitions = await sessionStorage.getItem('penalizedTransitions');
        const savedTransitions3 = await sessionStorage.getItem('penalizedTransitions_3');
        const savedTransitions4 = await sessionStorage.getItem('penalizedTransitions_4');
        const savedTransitions5 = await sessionStorage.getItem('penalizedTransitions_5');
        const savedPenalizedTransitions = await sessionStorage.getItem('penalizedTransitions_shifts');

        console.log('Transitions saved:', savedTransitions);
        console.log('Transitions3 saved:', savedTransitions3);
        console.log('Transitions4 saved:', savedTransitions4);
        console.log('Transitions5 saved:', savedTransitions5);
        console.log('Penalized transitions saved:', savedPenalizedTransitions);
      }
      return true;
    } catch (error) {
      console.error('Error saving transitions:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.saveError'),
        variant: 'destructive'
      });
      return false;
    }
  };

  useEffect(() => {
    const length = parseInt(sequenceLength);
    setCurrentTransition(Array(length).fill(0));
  }, [sequenceLength]);

  const handleTransitionChange = (index, value) => {
    if (isExampleMode) return;
    
    const newTransition = [...currentTransition];
    newTransition[index] = parseInt(value);
    setCurrentTransition(newTransition);
  };

  const handleAddTransition = () => {
    if (isExampleMode) return;
    
    if (currentTransition.some(idx => idx === null || idx === undefined)) {
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.selectAllShifts'),
        variant: 'destructive'
      });
      return;
    }
  
    const newTransition = [...currentTransition, 0];
    
    const isDuplicate = checkDuplicate(newTransition);
    if (isDuplicate) {
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.duplicateTransition'),
        variant: 'destructive'
      });
      return;
    }
  
    const length = parseInt(sequenceLength);
    let updatedArray;
    
    switch (length) {
      case 2:
        updatedArray = [...transitions, newTransition];
        setTransitions(updatedArray);
        sessionStorage.setItem("penalizedTransitions", updatedArray)
        break;
      case 3:
        updatedArray = [...transitions3, newTransition];
        setTransitions3(updatedArray);
        sessionStorage.setItem("penalizedTransitions3", updatedArray)
        break;
      case 4:
        updatedArray = [...transitions4, newTransition];
        setTransitions4(updatedArray);
        sessionStorage.setItem("penalizedTransitions4", updatedArray)
        break;
      case 5:
        updatedArray = [...transitions5, newTransition];
        setTransitions5(updatedArray);
        sessionStorage.setItem("penalizedTransitions5", updatedArray)
        break;
      default:
        break;
    }
  
    const shiftsSequence = currentTransition.map(idx => schedules[idx]?.code || 'Unknown');
    const updatedPenalizedTransitions = [
      ...penalizedTransitions, 
      { length, sequence: shiftsSequence, indices: [...newTransition] }
    ];
    setPenalizedTransitions(updatedPenalizedTransitions);
    
    if (!isExampleMode) {
      const transitionsToSave = length === 2 ? updatedArray : transitions;
      const transitions3ToSave = length === 3 ? updatedArray : transitions3;
      const transitions4ToSave = length === 4 ? updatedArray : transitions4;
      const transitions5ToSave = length === 5 ? updatedArray : transitions5;
      
      sessionStorage.setItem('penalizedTransitions', JSON.stringify(transitionsToSave));
      sessionStorage.setItem('penalizedTransitions_3', JSON.stringify(transitions3ToSave));
      sessionStorage.setItem('penalizedTransitions_4', JSON.stringify(transitions4ToSave));
      sessionStorage.setItem('penalizedTransitions_5', JSON.stringify(transitions5ToSave));
      sessionStorage.setItem('penalizedTransitions_shifts', JSON.stringify(updatedPenalizedTransitions));
    }
    
    setCurrentTransition(Array(length).fill(0));
  };

  const checkDuplicate = (newTransition) => {
    const length = newTransition.length - 1; 
    
    const targetArray = length === 2 ? transitions :
                       length === 3 ? transitions3 :
                       length === 4 ? transitions4 :
                       transitions5;
    
    return targetArray.some(existing => {
      return existing.slice(0, -1).every((val, idx) => val === newTransition[idx]);
    });
  };

  const handleDeleteTransition = (index) => {
    if (isExampleMode) return;
    
    const deletedTransition = penalizedTransitions[index];
    let updatedTransitions = [...transitions];
    let updatedTransitions3 = [...transitions3];
    let updatedTransitions4 = [...transitions4];
    let updatedTransitions5 = [...transitions5];
    
    switch (deletedTransition.length) {
      case 2:
        updatedTransitions = transitions.filter((_, i) => {
          const transitionIndices = transitions[i].slice(0, -1);
          const deletedIndices = deletedTransition.indices.slice(0, -1);
          return !arraysEqual(transitionIndices, deletedIndices);
        });
        setTransitions(updatedTransitions);
        break;
      case 3:
        updatedTransitions3 = transitions3.filter((_, i) => {
          const transitionIndices = transitions3[i].slice(0, -1);
          const deletedIndices = deletedTransition.indices.slice(0, -1);
          return !arraysEqual(transitionIndices, deletedIndices);
        });
        setTransitions3(updatedTransitions3);
        break;
      case 4:
        updatedTransitions4 = transitions4.filter((_, i) => {
          const transitionIndices = transitions4[i].slice(0, -1);
          const deletedIndices = deletedTransition.indices.slice(0, -1);
          return !arraysEqual(transitionIndices, deletedIndices);
        });
        setTransitions4(updatedTransitions4);
        break;
      case 5:
        updatedTransitions5 = transitions5.filter((_, i) => {
          const transitionIndices = transitions5[i].slice(0, -1);
          const deletedIndices = deletedTransition.indices.slice(0, -1);
          return !arraysEqual(transitionIndices, deletedIndices);
        });
        setTransitions5(updatedTransitions5);
        break;
      default:
        break;
    }
    
    const updatedPenalizedTransitions = penalizedTransitions.filter((_, i) => i !== index);
    setPenalizedTransitions(updatedPenalizedTransitions);
    
    sessionStorage.setItem('penalizedTransitions', JSON.stringify(updatedTransitions));
    sessionStorage.setItem('penalizedTransitions_3', JSON.stringify(updatedTransitions3));
    sessionStorage.setItem('penalizedTransitions_4', JSON.stringify(updatedTransitions4));
    sessionStorage.setItem('penalizedTransitions_5', JSON.stringify(updatedTransitions5));
  };

  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const handleNextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(prev => prev + 1);
    } else {
      setShowTutorial(false);
    }
  };

  const handleBack = () => {
    saveTransitions();
    navigate('/form/availability', {
      state: { isExample: isExampleMode }
    });
  };

  const handleNext = () => {
    if (saveTransitions()) {
      navigate('/form/staff', {
        state: { isExample: isExampleMode }
      });
    }
  };

  const getScheduleDisplayName = (index) => {
    const schedule = schedules[index];
    return schedule ? `${formatShiftCode(schedule.code)} (${schedule.legend || schedule.description || ''})` : 'Unknown';
  };

  const decimalToTimeString = (decimalTime) => {
    if (decimalTime === 0) return '00:00';
    if (decimalTime === 24) return '24:00';
    
    const hours = Math.floor(decimalTime);
    const minutes = Math.round((decimalTime - hours) * 60);
    
    if (minutes === 60) {
      return `${hours + 1}:00`;
    }
    
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatShiftCode = (shiftCode) => {
    if (!shiftCode) return '';
    
    let formatted = shiftCode.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)\s+(\d+)[.,](\d+)-(\d+)/g, 
      (match, prefix, prefixHours, prefixMins, startHours, startMins, endHours) => {
        const prefixTime = decimalToTimeString(parseFloat(`${prefixHours}.${prefixMins}`));
        const startTime = decimalToTimeString(parseFloat(`${startHours}.${startMins}`));
        return `(${prefix}${prefixTime}) ${startTime}-${endHours}:00`;
      }
    );
    
    formatted = formatted.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)\s+(\d+)-(\d+)[.,](\d+)/g, 
      (match, prefix, prefixHours, prefixMins, startHours, endHours, endMins) => {
        const prefixTime = decimalToTimeString(parseFloat(`${prefixHours}.${prefixMins}`));
        const endTime = decimalToTimeString(parseFloat(`${endHours}.${endMins}`));
        return `(${prefix}${prefixTime}) ${startHours}-${endTime}`;
      }
    );
    
    formatted = formatted.replace(/\(([A-Z]+)(\d+)[.,](\d+)\)/g, 
      (match, prefix, hours, mins) => {
        return `(${prefix}${decimalToTimeString(parseFloat(`${hours}.${mins}`))})`;
      }
    );
    
    formatted = formatted.replace(/(\d+)-(\d+)[.,](\d+)/g, 
      (match, start, hours, mins) => {
        return `${start}-${decimalToTimeString(parseFloat(`${hours}.${mins}`))}`;
      }
    );
    
    formatted = formatted.replace(/(\d+)[.,](\d+)/g, 
      (match, hours, mins) => {
        return decimalToTimeString(parseFloat(`${hours}.${mins}`));
      }
    );
    
    return formatted;
  };

  return (
    <div className="-mt-16">
      <Breadcrumbs pages={pages} />
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="flex justify-between items-center">
            <BackButton onClick={handleBack} />
            <div className="flex items-center gap-2 mb-6">
              <h1 className="text-2xl font-bold">
                {t("form.transitions.title") || "Shift Transitions"}
              </h1>
              <div className="flex items-center h-8">
                <Tooltip content={tooltips.transitions || "Define sequences of shifts that should not follow each other in the schedule."} />
              </div>
            </div>
            <NavigationButton ref={nextButtonRef} onClick={handleNext} />
          </div>

          <ScenarioIndicator />

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : schedules.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-amber-500 mb-4">
                  <AlertTriangle className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium">
                  {t("form.transitions.noSchedules") || "No schedules found"}
                </h3>
                <p className="text-gray-500 mt-2 mb-4">
                  {t("form.transitions.defineSchedulesFirst") || "Please define schedules first"}
                </p>
                <button
                  onClick={() => navigate('/form/schedule', { state: { isExample: isExampleMode } })}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  {t("form.transitions.defineSchedules") || "Define Schedules"}
                </button>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card ref={formRef}>
                <CardContent className="p-6">
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold mb-4">
                      {t("form.transitions.defineTransitions") || "Define Penalized Transitions"}
                    </h2>
                    <p className="text-sm text-gray-600 mb-4">
                      {t("form.transitions.description") || "Specify sequences of shifts that should not follow each other in the schedule."}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("form.transitions.sequenceLength") || "Sequence Length"}
                        </label>
                        <Select
                          value={sequenceLength}
                          onValueChange={setSequenceLength}
                          disabled={isExampleMode}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2">2 {t("form.transitions.shifts") || "Shifts"}</SelectItem>
                            <SelectItem value="3">3 {t("form.transitions.shifts") || "Shifts"}</SelectItem>
                            <SelectItem value="4">4 {t("form.transitions.shifts") || "Shifts"}</SelectItem>
                            <SelectItem value="5">5 {t("form.transitions.shifts") || "Shifts"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {currentTransition.map((_, index) => (
                        <div key={index}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("form.transitions.shift") || "Shift"} {index + 1}
                          </label>
                          <Select
                            value={currentTransition[index].toString()}
                            onValueChange={(value) => handleTransitionChange(index, value)}
                            disabled={isExampleMode}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {schedules.map((schedule, idx) => (
                                <SelectItem key={idx} value={idx.toString()}>
                                  {formatShiftCode(schedule.code)} ({schedule.legend || schedule.description || ''})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-end">
                      <button
                        onClick={handleAddTransition}
                        disabled={isExampleMode}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                          isExampleMode
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700'
                        }`}
                      >
                        <Plus className="h-4 w-4" />
                        {t("form.transitions.addTransition") || "Add Transition"}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {penalizedTransitions.length > 0 && (
                <Card ref={transitionsTableRef}>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4">
                      {t("form.transitions.definedTransitions") || "Defined Transitions"}
                    </h2>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t("form.transitions.sequence") || "Sequence"}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t("form.transitions.shifts") || "Shifts"}
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t("form.transitions.actions") || "Actions"}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {penalizedTransitions.map((transition, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {transition.length} {t("form.transitions.shiftsSequence") || "Shifts Sequence"}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900">
                                <div className="flex items-center">
                                  {transition.indices.slice(0, -1).map((shiftIndex, idx) => (
                                    <React.Fragment key={idx}>
                                      <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded">
                                        {getScheduleDisplayName(shiftIndex)}
                                      </span>
                                      {idx < transition.indices.length - 2 && (
                                        <span className="mx-1">→</span>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleDeleteTransition(index)}
                                  disabled={isExampleMode}
                                  className={`inline-flex items-center px-3 py-1 rounded-md transition-colors ${
                                    isExampleMode
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'bg-red-100 text-red-600 hover:bg-red-200'
                                  }`}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  {t("form.transitions.delete") || "Delete"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
      
      <TutorialOverlay 
        isVisible={showTutorial}
        onClose={() => setShowTutorial(false)}
        title={tutorialSteps[tutorialStep]?.title}
        description={tutorialSteps[tutorialStep]?.description}
        position={tutorialSteps[tutorialStep]?.position}
        targetRef={tutorialSteps[tutorialStep]?.elementRef}
        onNext={handleNextTutorialStep}
        isLastStep={tutorialStep === tutorialSteps.length - 1}
      />
    </div>
  );
};

export default TransitionsPage;